import { Component, Inject, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { SubscriptionsService } from '../../features/subscriptions/subscriptions.service';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, take } from 'rxjs';
import { Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import {
  CdkDragDrop,
  moveItemInArray,
  DragDropModule,
} from '@angular/cdk/drag-drop';
import {
  MatDialog,
  MatDialogContent,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { PermissionDirective } from '../../shared/components/directives/permissions.directives';
import * as selectPermissions from '../../core/permissions/permission.selector';
import { Store } from '@ngrx/store';
import { ContentLoaderComponent } from '../../shared/components/content-loader-component/contentLoader.component';

@Component({
  selector: 'app-subscription',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DragDropModule,
    PermissionDirective,
    ContentLoaderComponent,
  ],
  templateUrl: './subscription.component.html',
  styleUrl: './subscription.component.scss',
})

export class SubscriptionComponent implements OnInit {
  public isSubmitting: boolean = false;

  draggable$: Observable<boolean>;
  readonly dialog = inject(MatDialog);
  form!: FormGroup;
  contentLoading: boolean = true;
  plans: any[] = [];
  features: any[] = [];
  plansFeaturesMapping: any[] = [];
  plansFeaturesMappingUpdateList: any[] = [];

  constructor(
    private fb: FormBuilder,
    private subscriptionService: SubscriptionsService,
    private toastr: ToastrService,
    private store: Store,
    private router: Router
  ) {
    this.form = this.fb.group({
      features: this.fb.array([]),
    });
    this.draggable$ = this.store.select(
      selectPermissions.hasPermission('UPDATE_FEATURE')
    );
  }

  ngOnInit(): void {
    this.getPlans();
  }

  getPlans() {
    this.contentLoading = true;

    forkJoin({
      features: this.subscriptionService.getAllFeatures(),
      plans: this.subscriptionService.getAllPlans(),
      mappings: this.subscriptionService.getAllPlansFeaturesMapping(),
    }).subscribe({
      next: ({ features, plans, mappings }) => {
        this.features = features
          .map((feature: any) => ({
            id: feature.id,
            featureName: feature.featureTranslation[0]?.featureName ?? '',
            position: feature.position,
          }))
          .sort((a: any, b: any) => a.position - b.position);

        const PLAN_MAPPING: Record<string, string> = {
          'free': 'Free',
          'premium_monthly_rd': 'Premium',
          'premium_yearly_rds': 'Premium',
          'premium_plus_monthly_rd': 'Premium+',
          'premium_plus_yearly_rd': 'Premium+',
        };

        const ORDER = ['Free', 'Premium', 'Premium+'];

        this.plans = Object.values(
          plans
            .map((plan: any) => {
              const mappedName = PLAN_MAPPING[plan.planName] ?? plan.planName;
              return {
                id: plan.id,
                planName: mappedName,
              };
            })
            .reduce((acc: any, plan: any) => {
              acc[plan.planName] = plan; // dedupe by mapped name
              return acc;
            }, {})
        ).sort(
          (a: any, b: any) =>
            ORDER.indexOf(a.planName) - ORDER.indexOf(b.planName)
        );

        this.plansFeaturesMapping = mappings;
        this.contentLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.toastr.error('Failed to fetch subscription data');
        this.contentLoading = false;
      },
    });
  }

  openDialog(value: string, feature?: any): void {
    const dialogRef = this.dialog.open(SubscriptionDialogModal, {
      data: {
        type: value,
        ...(feature && { feature }),
        existingFeatures: this.features // Pass existing features for validation
      },
    });
    const sub = dialogRef.afterClosed().subscribe((shouldRefresh) => {
      if (shouldRefresh) {
        this.getPlans();
      }
      sub.unsubscribe();
    });
  }

  onDelete(id: any): void {
    const dialogRef = this.dialog.open(ConfirmDialog);

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.subscriptionService.deleteFeature(id).subscribe({
          next: (res) => {
            this.getPlans();
            this.toastr.success(res.message);
          },
          error: (err) => {
            this.toastr.error('Failed to delete feature');
            console.error(err);
          },
        });
      }
    });
  }

  updatePlansFeaturesMapping() {
    this.subscriptionService
      .updatePlansFeaturesMapping(this.plansFeaturesMappingUpdateList)
      .subscribe({
        next: (res) => {
          console.log(res);
        },
        error: (err) => {
          console.error(err);
        },
      });
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.features, event.previousIndex, event.currentIndex);
    this.features.forEach((feature, index) => {
      feature.position = index + 1;
    });
  }

  isChecked(targetPlanId: number, targetFeatureId: number) {
    const exists = this.plansFeaturesMapping.some(
      (item) =>
        item.isActive &&
        item.plan.id === targetPlanId &&
        item.feature.id === targetFeatureId
    );

    return exists;
  }

  onToggleCheckbox(planId: number, featureId: number, event: Event) {
    const isChecked = (event.target as HTMLInputElement)?.checked;
    this.plansFeaturesMappingUpdateList.push({
      planId: planId,
      featureId: featureId,
      isActive: isChecked,
    });
  }

  onSubmit() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;

    const checkboxRequest = this.subscriptionService.updatePlansFeaturesMapping(
      this.plansFeaturesMappingUpdateList
    );
    const positionPayload = this.features.map((feature) => ({
      id: feature.id,
      position: feature.position,
    }));
    const positionRequest =
      this.subscriptionService.updateFeaturePosition(positionPayload);

    forkJoin([checkboxRequest, positionRequest]).subscribe({
      next: ([checkboxRes, positionRes]) => {
        this.toastr.success('Plans and subscriptions updated successfully');
        this.plansFeaturesMappingUpdateList = [];
        this.getPlans()
      },
      error: (err) => {
        console.error(err);
        this.toastr.warning('All updates were not applied, please try again.');
      },
      complete: () => {
        this.isSubmitting = false;
      },
    });
  }

  onCancel() {
    this.plansFeaturesMappingUpdateList = [];
    this.getPlans()
  }
}

export class CustomValidators {
  static noWhitespace(control: AbstractControl): ValidationErrors | null {
    if (control.value && control.value.trim().length === 0) {
      return { whitespace: true };
    }
    return null;
  }

  static duplicateFeature(existingFeatures: any[], currentFeatureId?: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const inputValue = control.value.trim().toLowerCase();
      const isDuplicate = existingFeatures.some(
        (feature) =>
          feature.featureName.trim().toLowerCase() === inputValue &&
          feature.id !== currentFeatureId
      );

      return isDuplicate ? { duplicate: true } : null;
    };
  }

  static noSpecialCharacters(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const pattern = /^[a-zA-Z0-9\s\-\_\+\.\,\(\)]+$/;
    if (!pattern.test(control.value)) {
      return { specialCharacters: true };
    }
    return null;
  }
}

@Component({
  selector: 'app-profile',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogContent,
    MatFormField,
    MatInputModule,
    ReactiveFormsModule,
  ],
  styleUrl: './modal/dialog.component.scss',
  templateUrl: './modal/dialog.component.html',
})
export class SubscriptionDialogModal implements OnInit {
  readonly dialogRef = inject(MatDialogRef<SubscriptionDialogModal>);
  readonly dialog = inject(MatDialog);
  documentForm!: FormGroup;

  isSubmitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private subscriptionService: SubscriptionsService,
    private toastr: ToastrService
  ) {
    // Initialize form with comprehensive validators
    this.documentForm = this.fb.group({
      inputValue: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(50),
          Validators.pattern(/^[a-zA-Z ]+$/),
        ],
      ],
    });
  }

  ngOnInit(): void {
    const ctrl = this.documentForm.get('inputValue');

    if (this.data.type === 'add-plan' || this.data.type === 'add-feature' || this.data.type === 'edit-feature') {
      ctrl?.setValidators([
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        Validators.pattern(/^[a-zA-Z0-9 +]+$/),
      ]);
    }

    ctrl?.updateValueAndValidity();

    if (this.data.type === 'edit-feature') {
      ctrl?.patchValue(this.data.feature.featureName);
    }
  }

  get inputCtrl() {
    return this.documentForm.get('inputValue');
  }

  loading = false;

  onSave() {
    this.documentForm.markAllAsTouched();

    if (this.documentForm.invalid || this.isSubmitting) {
      return;
    }

    const value = this.documentForm.get('inputValue')!.value.trim();

    if (!value) {
      this.documentForm.get('inputValue')?.setErrors({ required: true });
      return;
    }

    this.isSubmitting = true;

    const payload: any = {
      translations: [{ featureName: value, langId: 1 }],
    };

    if (this.data.type === 'edit-feature') {
      payload.position = this.data.feature.position;
    }

    const request$ =
      this.data.type === 'add-feature'
        ? this.subscriptionService.createFeature(payload)
        : this.subscriptionService.updateFeature(this.data.feature.id, payload);

    request$.subscribe({
      next: (res) => {
        this.toastr.success(
          this.data.type === 'add-feature'
            ? 'Feature added successfully!'
            : 'Feature updated successfully!'
        );

        this.dialogRef.close(true);
      },
      error: () => {
        this.toastr.error(
          this.data.type === 'add-feature'
            ? 'Failed to add new feature'
            : 'Failed to update feature'
        );
        this.isSubmitting = false;
      },
      complete: () => {
        this.isSubmitting = false;
      },
    });
  }

  onNoClick(): void {
    this.dialogRef.close(false);
  }

  onCancel() {
    this.onNoClick();
  }
}
